import { hasHtmlTemplate, isExecutionEventContent, type MessageContent } from "@nexus/types/domain"
import type { PayloadAction } from "@reduxjs/toolkit"

import type { ReduxMessage } from "@/common/types/chat"

import type { EmbedChatState } from "../embedTypes"

/**
 * Returns true when a message's content carries a rendered HTML template card
 * (NEX-2541) — the one TOOL result that IS user-facing in the embed chat.
 *
 * 🔴 THE CARD TEST IS `@nexus/types`' `hasHtmlTemplate`, NOT A COPY OF IT. The backend's
 * `message-visibility.ts` admits a TOOL row to every customer-facing transcript by the
 * same guard, and the two once disagreed in silence — the widget showed a card the
 * transcript hid. One guard is what keeps them agreeing; `messages.test.ts` pins that
 * this widget decides a TOOL row by it.
 */
export function isHtmlTemplateCardContent(content: unknown): boolean {
  if (!content || typeof content !== "object" || Array.isArray(content)) return false
  return hasHtmlTemplate(content as MessageContent)
}

/**
 * Returns true for messages that must never appear in the embed chat UI:
 *
 * 1. TOOL messages — raw workflow results with a toolCallId; internal plumbing
 *    only. Exception: an HTML template card (NEX-2541) is the user-facing
 *    payload itself and renders as a rich message.
 * 2. Injected HUMAN messages — synthetic messages the backend injects as workflow
 *    output (statusInfos.injected === true). They contain raw JSON and are
 *    invisible to the end-user by design.
 * 3. Execution event envelopes, whatever role the row carries. Gating on
 *    `message.type` alone missed every envelope written onto a non-TOOL row,
 *    and the customer read the JSON as a message from the agent (NEX-3023).
 */
export function shouldHideEmbedMessage(message: {
  type?: string | null
  statusInfos?: unknown
  content?: unknown
}): boolean {
  if (isExecutionEventContent(message.content)) return true

  if (message.type === "TOOL") return !isHtmlTemplateCardContent(message.content)

  if (message.type === "HUMAN") {
    const statusInfos = message.statusInfos
    if (
      typeof statusInfos === "object" &&
      statusInfos !== null &&
      (statusInfos as Record<string, unknown>).injected === true
    ) {
      return true
    }
  }

  return false
}

/**
 * Add a single message to embed chat
 */
export const addEmbedMessage = (
  state: EmbedChatState,
  action: PayloadAction<{ chatId: string; message: ReduxMessage }>
) => {
  const { chatId, message } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return
  if (shouldHideEmbedMessage(message)) return

  const existingIndex = chat.messages.findIndex((m) => m.id === message.id)

  if (existingIndex >= 0) {
    chat.messages[existingIndex] = message
  } else {
    chat.messages.push(message)
  }

  if (message.type === "AI") {
    chat.shouldShowDynamicWhitespace = false
  }
}

/**
 * Set message history (append new messages, don't replace)
 */
export const setEmbedMessages = (
  state: EmbedChatState,
  action: PayloadAction<{ chatId: string; messages: ReduxMessage[] }>
) => {
  const { chatId, messages } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return

  const visibleMessages = messages.filter((m) => !shouldHideEmbedMessage(m))
  const existingMessages = chat.messages || []
  const newMessageIds = new Set(visibleMessages.map((m) => m.id))
  const filteredExisting = existingMessages.filter((m) => !newMessageIds.has(m.id))

  chat.messages = [...filteredExisting, ...visibleMessages]
}

/**
 * Add a single message from WebSocket (for AI responses)
 */
export const addEmbedMessageFromSocket = (
  state: EmbedChatState,
  action: PayloadAction<{ chatId: string; message: ReduxMessage }>
) => {
  const { chatId, message } = action.payload
  const chat = state.chats[chatId]

  if (!chat) return
  if (shouldHideEmbedMessage(message)) return

  const existingIndex = chat.messages.findIndex((m) => m.id === message.id)

  if (existingIndex >= 0) {
    chat.messages[existingIndex] = message
  } else {
    chat.messages.push(message)
  }
}
